<script setup lang="ts">
import { useElementSize } from "@vueuse/core";
import { type Component, computed, ref, watch } from "vue";

import Button from "~/components/dumb/Button.vue";
import FormError from "~/components/dumb/FormError.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { HideIcon, ShowIcon } from "~/icons";
import { ButtonStyle, CommandId, Placement } from "~/shared/enums";
import type { ValidationFunction } from "~/shared/types";
import { makeUuid } from "~/utils/common";
import { when } from "~/utils/wait";

const props = defineProps<{
  id?: string;
  initValue?: string;
  inputType?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  icon?: Component;
  iconArgs?: object;
  iconPosition?: "left" | "right";
  label?: string;
  largeLabel?: boolean;
  hideLabel?: boolean;
  hideLabelNonempty?: boolean;
  borderless?: boolean;
  isForm?: boolean;
  hideError?: boolean;
  showErrorAsTooltip?: boolean;
  inputClasses?: string;
  textClasses?: string;
  showErrorAlways?: boolean;
  dataTestid?: string;
  scaleWithHeight?: boolean;
  autocomplete?: string;
  maxLength?: number;
  hidePasswordToggle?: boolean;
  disabledGlobalShortcuts?: CommandId[] | true;
  validate?: ValidationFunction;
  transformValue?: (value: string) => string;
}>();

const emit = defineEmits<{
  blur: [];
  change: [newValue: string];
  click: [event: MouseEvent];
  enter: [event: KeyboardEvent];
  finalize: [newValue: string];
  focus: [];
  keydown: [event: KeyboardEvent];
}>();

const inputRef = ref<HTMLInputElement | null>(null);
const errorTooltip = ref<InstanceType<typeof Tooltip> | null>(null);

const value = ref(props.initValue ?? "");

watch(
  () => props.initValue,
  (newValue) => {
    if (newValue === undefined) {
      return;
    }
    value.value = newValue;
  }
);

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const newValue = target.value;

  value.value = props.transformValue ? props.transformValue(newValue) : newValue;
  emit("change", value.value);
};

const inputId = computed(() => props.id || `input-${makeUuid()}`);

const isPassword = computed(() => props.inputType === "password");
const showPasswordToggle = computed(() => isPassword.value && !props.hidePasswordToggle);
const showPassword = ref(false);
const typeNormalized = computed(() =>
  showPasswordToggle.value && showPassword.value ? "text" : (props.inputType ?? "text")
);

const iconPositionNorm = computed(() => props.iconPosition ?? "left");
const placeholderNorm = computed(() => props.placeholder ?? props.label);
const isTouched = ref(props.showErrorAlways);
const hasFocus = ref(false);

const validationResult = computed(() => props.validate?.(value.value));
const isValid = computed(() => {
  if (props.required && value.value === "") {
    return false;
  }
  return validationResult.value?.isValid ?? true;
});
const errorMsg = computed(() => {
  if (validationResult.value && !validationResult.value.isValid) {
    return validationResult.value.error;
  }

  if (props.required && value.value === "") {
    return "Enter a value";
  }

  return "";
});

const showErrorTooltip = computed(
  () => !!props.showErrorAsTooltip && hasFocus.value && !isValid.value && errorMsg.value.length > 0
);

const showErrorBorder = computed(() => (isTouched.value && !isValid.value) || showErrorTooltip.value);

const onFocus = () => {
  isTouched.value = props.showErrorAlways ?? false;
  hasFocus.value = true;
  emit("focus");
};

const onFocusout = () => {
  isTouched.value = true;
  hasFocus.value = false;
  emit("finalize", value.value);
};

const focus = async () => {
  await when(inputRef);
  inputRef.value?.focus();
};

const select = async () => {
  await when(inputRef);
  inputRef.value?.select();
};

const blur = async () => {
  await when(inputRef);
  inputRef.value?.blur();
};

const clear = () => {
  value.value = "";
};

// Scale font size with height
const { height: inputHeight } = useElementSize(inputRef);
const dynamicFontSize = computed(() => `${inputHeight.value * 0.7}px !important`);

watch(showErrorTooltip, (shown) => (shown ? errorTooltip.value?.show() : errorTooltip.value?.hide()));

defineExpose({
  focus,
  blur,
  clear,
  hasFocus,
  isTouched,
  isValid,
  select,
  value,
});
</script>

<template>
  <div class="relative flex flex-col">
    <label
      :for="inputId"
      :class="{
        'absolute -top-2 left-1.5 bg-std px-1 text-xs text-lt transition-opacity select-none': !largeLabel,
        'text-sm leading-7 font-medium text-md select-none': largeLabel,
        'sr-only': hideLabel || (hideLabelNonempty && !value),
      }">
      {{ `${label}${required ? " *" : ""}` }}
    </label>
    <input
      :id="inputId"
      ref="inputRef"
      v-model="value"
      v-disabled-global-shortcuts="disabledGlobalShortcuts"
      :type="typeNormalized"
      :required="required"
      :disabled="disabled"
      :placeholder="placeholderNorm"
      :autocomplete="autocomplete"
      :maxlength="maxLength"
      class="w-full rounded-sm border bg-transparent px-3 py-2 focus-ring-none placeholder:text-vlt"
      :class="[
        textClasses ?? (scaleWithHeight ? 'text-md' : 'text-sm text-md'),
        {
          'border-danger-base focus:border-danger-base': showErrorBorder,
          'hover:border-danger-hover-light dark:hover:border-danger-hover-dark': showErrorBorder && !disabled,
          'border-md focus:border-primary-base dark:focus:border-primary-base': !showErrorBorder && !borderless,
          'border-transparent focus:border-primary-base dark:focus:border-primary-base': !showErrorBorder && borderless,
          'hover:border-hvy': !showErrorBorder && !disabled,
          'pl-10': icon && iconPositionNorm === 'left',
          'pr-10': icon && iconPositionNorm === 'right',
          'min-h-8 !px-2 !py-[5px]': isForm,
          'pr-[38px]': showPasswordToggle,
        },
        inputClasses,
      ]"
      :style="scaleWithHeight ? { fontSize: dynamicFontSize } : {}"
     
      @click="emit('click', $event)"
      @keydown="emit('keydown', $event)"
      @focus="onFocus"
      @focusout="onFocusout"
      @keydown.enter="emit('enter', $event)"
      @keydown.enter.esc.prevent="inputRef?.blur()"
      @input="onInput"
      @blur="emit('blur')" />
    <div
      v-if="icon"
      class="pointer-events-none absolute inset-y-0 flex items-center"
      :class="iconPositionNorm === 'left' ? 'left-0 ml-3' : 'right-0 mr-3'">
      <component :is="icon" class="icon-md shrink-0 text-lt" v-bind="iconArgs" />
    </div>
    <div v-if="showPasswordToggle" class="absolute top-0 right-0 z-10 m-1.5">
      <Button
        :btn-style="ButtonStyle.CHIP"
        :icon="showPassword ? HideIcon : ShowIcon"
        borderless
        class="!p-0.5 text-vlt"
        a11y-label="Show password toggle"
        @click="showPassword = !showPassword" />
    </div>
    <FormError v-if="!hideError && validate" :msg="errorMsg" :show="isTouched && errorMsg.length > 0" />
    <Tooltip
      v-if="showErrorAsTooltip"
      ref="errorTooltip"
      no-wrapper
      show-on-mobile
      :reference-node="inputRef ?? undefined"
      :text="errorMsg"
      :placement="Placement.BOTTOM" />
  </div>
</template>
